/* eslint-disable */

import { NavLink, useLocation } from 'react-router-dom';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Flex,
  HStack,
  Icon,
  List,
  ListItem,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaCircle } from 'react-icons/fa';

export function SidebarLinks(props: { routes: RoutesType[]; [x: string]: any }) {
  const location = useLocation();
  const { routes, mini } = props;

  const activeColor = useColorModeValue('#7a1b31', 'white');
  const inactiveColor = useColorModeValue('#5e626b', 'secondaryGray.500');
  const activeBg = useColorModeValue('linear-gradient(135deg, rgba(161,38,61,.14), rgba(215,155,82,.20))', 'whiteAlpha.200');
  const hoverBg = useColorModeValue('rgba(161,38,61,.07)', 'whiteAlpha.100');
  const borderColor = useColorModeValue('rgba(161,38,61,.18)', 'whiteAlpha.200');

  const activeRoute = (routeName: string) => {
    const route = routeName.toLowerCase();
    return location.pathname.toLowerCase().includes(route);
  };

  const hasActiveChild = (route: RoutesType) => {
    if (!route.items) return false;
    return route.items.some((item) => activeRoute(item.path.toLowerCase()));
  };

  const renderIcon = (route: RoutesType, active: boolean) => (
    <Tooltip label={route.name} placement="right" isDisabled={!mini}>
      <Flex
        className="sidebar-icon"
        align="center"
        justify="center"
        w="34px"
        h="34px"
        borderRadius="13px"
        color={active ? activeColor : inactiveColor}
        bg={active ? 'whiteAlpha.700' : 'transparent'}
        boxShadow={active ? '0 8px 18px rgba(122,27,49,.12)' : 'none'}
        flexShrink={0}
      >
        {route.icon}
      </Flex>
    </Tooltip>
  );

  const itemShell = (route: RoutesType, active: boolean, children: React.ReactNode) => (
    <Flex
      align="center"
      w="100%"
      minH="46px"
      px={mini ? 2 : 3}
      py="6px"
      mb="6px"
      borderRadius="16px"
      bg={active ? activeBg : 'transparent'}
      border="1px solid"
      borderColor={active ? borderColor : 'transparent'}
      color={active ? activeColor : inactiveColor}
      position="relative"
      transition="all .18s ease"
      _hover={{
        bg: active ? activeBg : hoverBg,
        color: activeColor,
        transform: mini ? 'none' : 'translateX(2px)',
      }}
      _before={active && !mini ? {
        content: '""',
        position: 'absolute',
        left: '-8px',
        top: '11px',
        bottom: '11px',
        width: '4px',
        borderRadius: 'full',
        bg: '#A1263D',
      } : undefined}
    >
      {children}
    </Flex>
  );

  const createLinks = (items: RoutesType[]) => {
    const accordionItems = items.filter((route) => route.collapse);
    const regularItems = items.filter((route) => !route.collapse);

    return (
      <>
        {accordionItems.length > 0 && (
          <Accordion allowToggle defaultIndex={accordionItems.findIndex(hasActiveChild) >= 0 ? [accordionItems.findIndex(hasActiveChild)] : undefined}>
            {accordionItems.map((route, key) => {
              const active = activeRoute(route.path.toLowerCase()) || hasActiveChild(route);
              return (
                <AccordionItem maxW="100%" border="none" key={`${route.name}-${key}`}>
                  <AccordionButton p={0} _hover={{ bg: 'transparent' }} _focus={{ boxShadow: 'none' }}>
                    {itemShell(route, active, (
                      <HStack w="100%" spacing={mini ? 0 : 3} justify={mini ? 'center' : 'flex-start'}>
                        {route.icon && renderIcon(route, active)}
                        {!mini && (
                          <>
                            <Text className="sidebar-text" flex="1" textAlign="left" fontWeight={active ? '800' : '650'} fontSize="sm" noOfLines={1}>
                              {route.name}
                            </Text>
                            <AccordionIcon color={active ? activeColor : inactiveColor} />
                          </>
                        )}
                      </HStack>
                    ))}
                  </AccordionButton>
                  <AccordionPanel px={mini ? 1 : 3} pt={0} pb={1}>
                    <List borderLeft={mini ? '0' : '1px solid'} borderColor={borderColor} ml={mini ? 0 : 4} pl={mini ? 0 : 3}>
                      {route.icon ? createLinks(route.items || []) : createAccordionLinks(route.items || [])}
                    </List>
                  </AccordionPanel>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}

        {regularItems.map((route, key) => {
          const active = activeRoute(route.path.toLowerCase());
          return (
            <NavLink to={route.layout + route.path} key={`${route.name}-${key}`}>
              {route.icon ? (
                itemShell(route, active, (
                  <HStack w="100%" spacing={mini ? 0 : 3} justify={mini ? 'center' : 'flex-start'}>
                    {renderIcon(route, active)}
                    {!mini && (
                      <Text className="sidebar-text" flex="1" fontWeight={active ? '800' : '650'} fontSize="sm" noOfLines={1}>
                        {route.name}
                      </Text>
                    )}
                  </HStack>
                ))
              ) : (
                <ListItem mb="7px">
                  <Flex px={mini ? 0 : 3} py={2} borderRadius="12px" bg={active ? hoverBg : 'transparent'} color={active ? activeColor : inactiveColor}>
                    <Text fontWeight={active ? '800' : '600'} fontSize="sm" noOfLines={1}>
                      {mini ? route.name[0] : route.name}
                    </Text>
                  </Flex>
                </ListItem>
              )}
            </NavLink>
          );
        })}
      </>
    );
  };

  const createAccordionLinks = (items: RoutesType[]) => {
    return items.map((route: RoutesType, key: number) => {
      const active = activeRoute(route.path.toLowerCase());
      return (
        <NavLink to={route.layout + route.path} key={`${route.name}-${key}`}>
          <ListItem display="flex" alignItems="center" mb="8px">
            <Flex
              w="100%"
              align="center"
              gap={2}
              px={mini ? 0 : 2}
              py="7px"
              borderRadius="12px"
              bg={active ? hoverBg : 'transparent'}
              color={active ? activeColor : inactiveColor}
              _hover={{ bg: hoverBg, color: activeColor }}
            >
              <Icon w="6px" h="6px" as={FaCircle} color={active ? '#A1263D' : '#c6b9ae'} />
              {!mini && (
                <Text fontWeight={active ? '800' : '600'} fontSize="sm" noOfLines={1}>
                  {route.name}
                </Text>
              )}
            </Flex>
          </ListItem>
        </NavLink>
      );
    });
  };

  return createLinks(routes);
}

export default SidebarLinks;
